'use client';

/**
 * @fileoverview `useSearchSheet` — open/close state hook backing the global
 * `SearchSheet` overlay organism (WEB-220).
 *
 * The hook is the single source of truth shared between:
 *   - `AppHeader` (WEB-218): triggers the sheet via `setOpen(true)` /
 *     `openSheet()` from the header search button.
 *   - `SearchSheet` (WEB-220): consumes `open`, mirrors `onOpenChange`
 *     into the store, and calls `closeSheet()` on result selection.
 *
 * **Canonical contract for cross-issue coordination:**
 * ```ts
 * useSearchSheet().setOpen(boolean)
 * ```
 * Must work; everything else is implementation detail.
 *
 * **Trigger source (FRE-822 telemetry).** Callers MAY pass an optional
 * `{ trigger: 'button' | 'kbd' | 'slash' }` second argument to `setOpen`
 * / `openSheet`. The trigger is stored on the same module-scoped store and
 * exposed as `openTrigger` so the SearchSheet can fire a `search_opened`
 * PostHog event with the correct origin. Omitting the trigger preserves
 * backward compatibility with the WEB-220 call sites that pre-date the
 * telemetry capture-map.
 *
 * The store is a tiny module-scoped pub/sub backed by `useSyncExternalStore`,
 * so:
 *   - There is no provider to add to `app/layout.tsx`.
 *   - SSR returns `false` for `open` (server-safe), preventing hydration
 *     mismatches.
 *   - All consumers see the same value without prop drilling.
 *
 * Zustand was considered (the issue suggests it), but the web app does not
 * already depend on zustand. A 30-line `useSyncExternalStore` store keeps
 * the bundle lean while preserving the same external API.
 */

import * as React from 'react';

/**
 * How the SearchSheet was opened. Mirrors the trigger source emitted on the
 * FRE-822 `search_opened` PostHog event.
 *
 * - `'button'` — the AppHeader / SiteHeader search button.
 * - `'kbd'` — a keyboard shortcut (e.g. ⌘K — FRE-G).
 * - `'slash'` — the `/` quick-open shortcut.
 */
export type SearchSheetOpenTrigger = 'button' | 'kbd' | 'slash';

interface SearchSheetOpenOptions {
  /** Where the open request came from (FRE-822 telemetry). */
  readonly trigger?: SearchSheetOpenTrigger;
}

interface SearchSheetState {
  /** Whether the search sheet overlay is currently open. */
  readonly open: boolean;
  /**
   * The trigger source recorded by the most recent `setOpen(true, …)` /
   * `openSheet(…)` call. Cleared back to `undefined` whenever the sheet
   * closes, so the next open cycle starts from a clean slate. Only
   * meaningful while `open === true`.
   */
  readonly openTrigger: SearchSheetOpenTrigger | undefined;
  /**
   * Imperative setter — accepts a boolean (matches Radix `onOpenChange`)
   * and an optional `{ trigger }` source for FRE-822 telemetry. The
   * `trigger` argument is ignored when `open === false`.
   */
  readonly setOpen: (open: boolean, opts?: SearchSheetOpenOptions) => void;
  /** Convenience: `setOpen(true, opts)`. */
  readonly openSheet: (opts?: SearchSheetOpenOptions) => void;
  /** Convenience: `setOpen(false)`. */
  readonly closeSheet: () => void;
}

type Listener = () => void;

/* ------------------------------------------------------------------ */
/*  Module-scoped store — single global instance                       */
/* ------------------------------------------------------------------ */

let currentOpen = false;
let currentOpenTrigger: SearchSheetOpenTrigger | undefined;
const listeners = new Set<Listener>();

function notifyListeners(): void {
  // Snapshot listeners before iterating so a listener that detaches itself
  // mid-flush does not skip a sibling.
  for (const listener of [...listeners]) listener();
}

function setOpenImpl(next: boolean, opts?: SearchSheetOpenOptions): void {
  const nextTrigger = next ? opts?.trigger : undefined;
  if (currentOpen === next && currentOpenTrigger === nextTrigger) return;
  currentOpen = next;
  currentOpenTrigger = nextTrigger;
  notifyListeners();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Snapshot tuple. `useSyncExternalStore` compares snapshots by reference,
 * so we cache the tuple and only mint a new one when the underlying state
 * actually changes — this prevents the consumer from re-rendering on every
 * unrelated re-render of its parent.
 */
type Snapshot = readonly [open: boolean, openTrigger: SearchSheetOpenTrigger | undefined];

let cachedSnapshot: Snapshot = [false, undefined];

function getSnapshot(): Snapshot {
  if (cachedSnapshot[0] === currentOpen && cachedSnapshot[1] === currentOpenTrigger) {
    return cachedSnapshot;
  }
  cachedSnapshot = [currentOpen, currentOpenTrigger];
  return cachedSnapshot;
}

/**
 * Server snapshot — always the stable closed tuple so SSR markup matches
 * the initial client render and React doesn't emit a hydration warning.
 * Must be referentially stable across calls or React will warn about
 * server snapshot identity.
 */
const SERVER_SNAPSHOT: Snapshot = [false, undefined];

function getServerSnapshot(): Snapshot {
  return SERVER_SNAPSHOT;
}

/* ------------------------------------------------------------------ */
/*  Public hook                                                         */
/* ------------------------------------------------------------------ */

/**
 * React hook for the global SearchSheet open/close state.
 *
 * @example
 * ```tsx
 * // In AppHeader (WEB-218) — wired with FRE-822 trigger source
 * const { openSheet } = useSearchSheet();
 * <button onClick={() => openSheet({ trigger: 'button' })}>Search CLT…</button>
 *
 * // In SearchSheet (WEB-220)
 * const { open, openTrigger, setOpen } = useSearchSheet();
 * <Dialog open={open} onOpenChange={setOpen}>...</Dialog>
 * ```
 */
export function useSearchSheet(): SearchSheetState {
  const [open, openTrigger] = React.useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  // Stable callbacks — same identity for the lifetime of the module so
  // `useEffect` deps that include them never re-fire unnecessarily.
  const setOpen = React.useCallback((next: boolean, opts?: SearchSheetOpenOptions) => {
    setOpenImpl(next, opts);
  }, []);
  const openSheet = React.useCallback((opts?: SearchSheetOpenOptions) => {
    setOpenImpl(true, opts);
  }, []);
  const closeSheet = React.useCallback(() => {
    setOpenImpl(false);
  }, []);

  return { open, openTrigger, setOpen, openSheet, closeSheet };
}

/* ------------------------------------------------------------------ */
/*  Test utilities — exported for unit tests, not for runtime use       */
/* ------------------------------------------------------------------ */

/**
 * Reset the store between tests. **Do not call from production code.**
 * @internal
 */
export function __resetSearchSheetStateForTests(): void {
  setOpenImpl(false);
}
